import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

// Add CSS for Safari compatibility
const safariStyles = `
  .gradient-text {
    background: linear-gradient(45deg, #ff6b9d, #c44569, #f8b500);
    background: -webkit-linear-gradient(45deg, #ff6b9d, #c44569, #f8b500);
    background: -moz-linear-gradient(45deg, #ff6b9d, #c44569, #f8b500);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }
  
  /* Fallback for browsers that don't support background-clip: text */
  @supports not (-webkit-background-clip: text) {
    .gradient-text {
      color: #ff6b9d !important;
      background: none !important;
    }
  }
  
  .gradient-bg {
    background: #581c87;
    background: linear-gradient(135deg, #581c87 0%, #be185d 50%, #991b1b 100%);
    background: -webkit-linear-gradient(135deg, #581c87 0%, #be185d 50%, #991b1b 100%);
    background: -moz-linear-gradient(135deg, #581c87 0%, #be185d 50%, #991b1b 100%);
  }
`

const CongPage = () => {
  const [showHearts, setShowHearts] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowHearts(true), 2000)
    return () => clearTimeout(timer)
  }, [])

  const letterVariants = {
    hidden: { opacity: 0, y: 50, rotateX: -90 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        type: "spring" as const,
        stiffness: 100
      }
    })
  }

  const heartVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: [0, 1.2, 1],
      opacity: [0, 1, 0.8],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "reverse" as const
      }
    }
  }

  const glowVariants = {
    animate: {
      textShadow: [
        "0 0 20px #ff6b9d, 0 0 40px #ff6b9d, 0 0 60px #ff6b9d",
        "0 0 30px #c44569, 0 0 60px #c44569, 0 0 90px #c44569",
        "0 0 20px #ff6b9d, 0 0 40px #ff6b9d, 0 0 60px #ff6b9d"
      ],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut" as const
      }
    }
  }

  const text = "I LOVE YOU CONG"
  const letters = text.split("")

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: safariStyles }} />
      <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden gradient-bg">
        {/* Animated background particles */}
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-pink-400 rounded-full"
              initial={{
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight,
                opacity: 0
              }}
              animate={{
                y: [null, -100],
                opacity: [0, 1, 0],
                scale: [0, 1, 0]
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>

        {/* Main content */}
        <div className="text-center z-10">
          <motion.div
            className="text-6xl md:text-8xl lg:text-9xl font-bold mb-8"
            variants={glowVariants}
            animate="animate"
          >
            {letters.map((letter, i) => (
              <motion.span
                key={i}
                className="inline-block gradient-text"
                variants={letterVariants}
                initial="hidden"
                animate="visible"
                custom={i}
              >
                {letter === " " ? "\u00A0" : letter}
              </motion.span>
            ))}
          </motion.div>

          {/* Floating hearts */}
          {showHearts && (
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute text-4xl"
                  initial={{
                    x: Math.random() * window.innerWidth,
                    y: window.innerHeight + 50
                  }}
                  animate={{
                    y: -100,
                    x: Math.random() * window.innerWidth
                  }}
                  transition={{
                    duration: Math.random() * 4 + 3,
                    repeat: Infinity,
                    delay: Math.random() * 2
                  }}
                  variants={heartVariants}
                >
                  💖
                </motion.div>
              ))}
            </div>
          )}

          {/* Pulsing subtitle */}
          <motion.p
            className="text-xl md:text-2xl text-pink-200 mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.5, duration: 1 }}
          >
            <motion.span
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              Forever and Always ✨
            </motion.span>
          </motion.p>
        </div>

        {/* Corner hearts */}
        <motion.div
          className="absolute top-10 left-10 text-6xl"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 15, -15, 0]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          💕
        </motion.div>

        <motion.div
          className="absolute top-10 right-10 text-6xl"
          animate={{
            y: [0, -20, 0],
            rotate: [0, -15, 15, 0]
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          💖
        </motion.div>

        <motion.div
          className="absolute bottom-10 left-10 text-6xl"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 15, -15, 0]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          💝
        </motion.div>

        <motion.div
          className="absolute bottom-10 right-10 text-6xl"
          animate={{
            y: [0, -20, 0],
            rotate: [0, -15, 15, 0]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          💗
        </motion.div>
      </div>
    </>
  )
}

export default CongPage